import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { InputSearchComponent } from "../../../../shared/modules/input-search/input-search.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { SPECIFICATION_LIST_DATA } from "../../models/data.model";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { EtatSpecification } from "../../../specifications/enums/etat-specif.enum";
import { exportSpecificationListExcel } from "./specification-list-excel.utils";

export type SpecState = "REDIGEE" | "VALIDEE" | "ALL";
export type SpecSortKey = "denomination" | "version" | "etat" | "date";

/** Ligne plate exposée à l'excel — `etat` ne peut pas être "ALL" ici. */
export interface SpecRow {
  id: string;
  articleId: string | null;
  denomination: string;
  code: string | null;
  versionNb: number | null;
  etat: "REDIGEE" | "VALIDEE";
  date: Date | null;
}

export interface SpecificationListDialogData {
  data: SPECIFICATION_LIST_DATA;
  initialSubType?: "REDIGEE" | "VALIDEE";
}

export interface SpecificationListDialogResult {
  id: string;
  articleId: string | null;
}

@Component({
  selector: "vl-specification-list-dialog",
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    SharedModule,
    DialogHeaderExportComponent,
    ChipsComponent,
    InputSearchComponent,
    RadioCardComponent,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: "./specification-list-dialog.component.html",
  styleUrl: "./specification-list-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecificationListDialogComponent {
  data = inject<SpecificationListDialogData>(MAT_DIALOG_DATA);
  dialogRef =
    inject<MatDialogRef<SpecificationListDialogComponent, SpecificationListDialogResult | null>>(
      MatDialogRef,
    );
  private articleInfosFacade = inject(ArticlesInfosFacadeService);

  exporting = signal(false);
  search = signal<string>("");
  sortKey = signal<SpecSortKey>("date");
  sortDesc = signal<boolean>(true);
  subType = signal<SpecState>(this.data.initialSubType ?? "ALL");

  /** Convertit chaque spécification en lignes plates : 1 ligne par version (REDIGEE / VALIDEE / APPROUVEE). */
  private allRows = computed<SpecRow[]>(() => {
    const list = this.data.data?.specifications ?? [];
    const rows: SpecRow[] = [];
    for (const s of list) {
      const article = s.articleId
        ? this.articleInfosFacade.getByArticleId(s.articleId)?.()
        : null;
      const denom = article?.denomination ?? s.denomination ?? "—";
      const code = article?.code ?? null;

      for (const v of s.versions ?? []) {
        if (
          v.etat === EtatSpecification.REDIGEE ||
          v.etat === EtatSpecification.APPROUVEE ||
          v.etat === EtatSpecification.VALIDEE
        ) {
          const isValide =
            v.etat === EtatSpecification.APPROUVEE ||
            v.etat === EtatSpecification.VALIDEE;
          rows.push({
            id: s.id,
            articleId: s.articleId ?? null,
            denomination: denom,
            code,
            versionNb: v.version_nb ?? null,
            etat: isValide ? "VALIDEE" : "REDIGEE",
            date:
              (isValide ? v.dateValidation : v.dateRedaction) ??
              v.dateRedaction ??
              null,
          });
        }
      }
    }
    return rows;
  });

  filtered = computed<SpecRow[]>(() => {
    const all = this.allRows();
    const q = this.search().trim().toLowerCase();
    const sub = this.subType();

    let list = sub === "ALL" ? all : all.filter((r) => r.etat === sub);
    if (q) {
      list = list.filter(
        (r) =>
          r.denomination.toLowerCase().includes(q) ||
          (r.code ?? "").toLowerCase().includes(q),
      );
    }
    return this.sortList(list);
  });

  stats = computed(() => {
    const all = this.allRows();
    const total = all.length;
    const redigees = all.filter((r) => r.etat === "REDIGEE").length;
    const validees = all.filter((r) => r.etat === "VALIDEE").length;

    let mostRecent: SpecRow | null = null;
    for (const r of all) {
      if (!r.date) continue;
      if (!mostRecent?.date || r.date > mostRecent.date) {
        mostRecent = r;
      }
    }

    return {
      total,
      redigees,
      validees,
      mostRecentDenom: mostRecent?.denomination ?? "—",
      mostRecentDate: mostRecent?.date ?? null,
    };
  });

  subTypeOptions = computed(() => {
    const all = this.allRows();
    return [
      { name: "Toutes", value: "ALL" as SpecState, count: all.length },
      {
        name: "Rédigées",
        value: "REDIGEE" as SpecState,
        count: all.filter((r) => r.etat === "REDIGEE").length,
      },
      {
        name: "Validées",
        value: "VALIDEE" as SpecState,
        count: all.filter((r) => r.etat === "VALIDEE").length,
      },
    ];
  });

  toggleSort(key: SpecSortKey): void {
    if (this.sortKey() === key) {
      this.sortDesc.set(!this.sortDesc());
    } else {
      this.sortKey.set(key);
      this.sortDesc.set(key === "date" || key === "version");
    }
  }

  private sortList(list: SpecRow[]): SpecRow[] {
    const key = this.sortKey();
    const desc = this.sortDesc();
    const mult = desc ? -1 : 1;
    return [...list].sort((a, b) => {
      switch (key) {
        case "denomination":
          return a.denomination.localeCompare(b.denomination) * mult;
        case "version":
          return ((a.versionNb ?? 0) - (b.versionNb ?? 0)) * mult;
        case "etat":
          return a.etat.localeCompare(b.etat) * mult;
        case "date": {
          const ta = a.date ? a.date.getTime() : 0;
          const tb = b.date ? b.date.getTime() : 0;
          return (ta - tb) * mult;
        }
      }
    });
  }

  etatLabel(e: SpecState): string {
    return e === "REDIGEE" ? "Rédigée" : "Validée";
  }

  onSelect(row: SpecRow): void {
    this.dialogRef.close({ id: row.id, articleId: row.articleId });
  }

  async exportExcel(): Promise<void> {
    this.exporting.set(true);
    try {
      await exportSpecificationListExcel({
        rows: this.filtered(),
        allSpecifications: this.data.data?.specifications ?? [],
        resolveDenom: (id: string) =>
          this.articleInfosFacade.getByArticleId(id)?.()?.denomination ?? null,
        resolveCode: (id: string) =>
          this.articleInfosFacade.getByArticleId(id)?.()?.code ?? null,
        stats: this.stats(),
        scope:
          this.subType() === "ALL"
            ? "Toutes"
            : this.subType() === "REDIGEE"
              ? "Rédigées"
              : "Validées",
      });
    } finally {
      this.exporting.set(false);
    }
  }
}
